import { IGroupedOption } from "app/components/multiselect-dropdown/multiselect-dropdown.component";

export type Action = "Create" | "Modify" | "Delete" | "Authentication" | "KeyCode" | "GroupSelection" | "Pause" | "Resume" | "Unpaired" | "Erased" | "Replaced";
export type Area = "Staff" | "Portal" | "EMA" | "Digital Supervision" | "AlarmRoute" | "DigitalKeys" | "AlarmUserGroup" | "Location" | "Devices" | "AccessRole" | "MobileWhitelist";
export type ReportType = "UserActivityPortal";

export type ActivityTranslationCode =
  | "STAFF_ACCOUNT_MODIFIED"
  | "STAFF_ACCOUNT_CREATED"
  | "STAFF_ACCOUNT_DELETED"
  | "USER_LOGGED_IN"
  | "USER_LOGGED_OUT"
  | "USER_LOGIN_FAILED"
  | "CAMERA_GROUP_CREATED"
  | "CAMERA_GROUP_DELETED"
  | "CAMERA_GROUP_MODIFIED"
  | "SCHEDULED_VISIT_CREATED"
  | "SCHEDULED_VISIT_DELETED"
  | "SCHEDULED_VISIT_MODIFIED"
  | "ALARM_ROUTE_CREATED"
  | "ALARM_ROUTE_DELETED"
  | "ALARM_ROUTE_MODIFIED"
  | "TEMPORARY_CODE_GENERATED"
  | "ALARM_USER_GROUP_CREATED"
  | "ALARM_USER_GROUP_MODIFIED"
  | "ALARM_USER_GROUP_DELETED"
  | "LOCATION_CREATED"
  | "LOCATION_MODIFIED"
  | "LOCATION_REMOVED"
  | "GROUP_SELECTION"
  | "MANUAL_PAUSE"
  | "MANUAL_RESUME"
  | "SCHEDULED_PAUSE_CREATED"
  | "SCHEDULED_PAUSE_MODIFIED"
  | "SCHEDULED_PAUSE_CANCELLED"
  | "SCHEDULED_AUTO_PAUSE_ACTIVATION"
  | "SCHEDULED_AUTO_RESUME_ACTIVATION"
  | "DEVICE_UNPAIRED"
  | "DEVICE_ERASED"
  | "DEVICE_REPLACED"
  | "DOOR_ID_OVERRIDE"
  | "ACCESS_ROLE_CREATED"
  | "ACCESS_ROLE_MODIFIED"
  | "ACCESS_ROLE_DELETED"
  | "STAFF_ACCESS_ROLE_ASSIGNED"
  | "STAFF_ACCESS_OVERRIDES_MODIFIED"
  | "MOBILE_WHITELIST_REQUESTED"
  | "MOBILE_WHITELIST_APPROVED"
  | "MOBILE_WHITELIST_DENIED"
  | "MOBILE_WHITELIST_BLOCKED"
  | "MOBILE_WHITELIST_MODIFIED";
    


export const VISIBLE_DETAILS_FIELDS_COUNT = 10;

// Not in use at the moment
export const REPORT_TYPE_OPTIONS: { value: ReportType; name: string }[] = [
  { value: "UserActivityPortal", name: "PORTAL_USER_ACTIVITY" },
];

// Note: options needs to alphebetically ordered
export const AREA_OPTIONS: { value: Area; name: string }[] = [
  { value: "AccessRole", name: "ACCESS_ROLES" },
  { value: "AlarmRoute", name: "ALARM_ROUTES" },
  { value: "Devices", name: "DEVICES" },
  { value: "DigitalKeys", name: "DIGITAL_KEYS" },
  { value: "Digital Supervision", name: "DIGITAL_SUPERVISION" },
  { value: "EMA", name: "EMA" },
  { value: "MobileWhitelist", name: "MOBILE_WHITELIST" },
  { value: "Portal", name: "Everon Portal" },
  { value: "Staff", name: "STAFF" },
  { value: "AlarmUserGroup", name: "ALARM_USER_GROUP" },
  { value: "Location", name: "LOCATION" },
];

// Note: options needs to alphebetically ordered
export const ACTION_OPTIONS: { value: Action; name: string }[] = [
  { value: "Authentication", name: "AUTHENTICATION" },
  { value: "Create", name: "CREATED" },
  { value: "Delete", name: "DELETED" },
  { value: "Erased", name: "ERASED" },
  { value: "GroupSelection", name: "ACTIVITY_GROUP_SELECTION" },
  { value: "KeyCode", name: "KEY_CODE" },
  { value: "Modify", name: "MODIFIED" },
  { value: "Pause", name: "ACTIVITY_PAUSE" },
  { value: "Resume", name: "ACTIVITY_RESUME" },
  { value: "Unpaired", name: "UNPAIRED" },
];

export enum ActivityTargetClass {
  CameraGroup = "Camera group",
  ScheduledVisit = "Scheduled visit",
  VisitPause = "Visit pause",
}

export class ActivityLogFilters {
  reportTypes: ReportType[] = [];
  users: string[] = [];
  cameraGroups: string[] = [];
  areas: Area[] = [];
  actions: Action[] = [];
  search: string = "";
  pageIndex: number = 0;
  pageSize: number = 25;
  startDate: string | null = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split("T")[0];
  endDate: string | null = null;
}

export class ActivityLogFilterOptions {
  actions: { value: Action; name: string }[] = ACTION_OPTIONS;
  areas: { value: Area; name: string }[] = AREA_OPTIONS;
  reportTypes: { value: ReportType; name: string }[] = REPORT_TYPE_OPTIONS;
  users: IGroupedOption[] = [];
  cameraGroups: any[] = [];
}

export interface IActivityLog {
  id: string;
  timestamp: string;
  companyId: string;
  staffId: string;
  staffName: string;
  area: Area;
  details: IActivityDetails;
  action: Action;
}

export interface IActivityDetails {
  // The translation code of the activity related action
  translationCode: string;
  // The name of the target entity e.g. staff name
  targetName: string;
  // The id of the target entity e.g. staff id
  targetId: string;
  // Class for which the target belongs to, e.g. Camera group, Sheduled visit
  targetClass?: ActivityTargetClass;
  // The role level of the target entity e.g. staff role level
  roleLevel?: number;
  // Changes made to the user account
  accountChanges?: IUserAccountChangeRecord;
  // Changes made to the camera group
  cameraGroupChanges?: ICameraGroupChangeRecord;
  // Activity details related to the scheduled visit
  scheduledVisitChanges?: IScheduledVisitChangeRecord;
  // Changes made to a scheduled visit (Edit action only)
  scheduledVisitEditChanges?: IScheduledVisitEditChangeRecord;
  // Changes made to the camera groups pause/resume actions
  cameraGroupPauseChanges?: ICameraGroupPauseChangeRecord;
  // Changes made to the alarm route
  alarmRouteChanges?: IAlarmRouteChangeRecord;
  // Changes made to the receiver groups
  alarmUserGroupChanges?: IAlarmUserGroupChangeRecord;
  // Changes made to the location
  locationChanges?: ILocationChangeRecord;
  emaSettingsChanges?: IEmaSettingsChangeRecord;
  locationPersonnelChanges?: IPersonnelChangeRecord;
  deviceChanges?: {
    deviceName?: string;
    deviceId?: string;
    deviceType?: string;
    replacedWithDeviceId?: string;
    newDoorId?: string;
    defectiveDoorId?: string;
    removedActivations?: AlertType[] | RemovedActivationRoute[];
  };
  // Changes made to an access role, a staff member's role or individual permissions
  accessRoleChanges?: IAccessRoleChangeRecord;
  // EMA phone whitelist change: the phone, and the old and new name and number when they changed
  mobileWhitelistChanges?: IMobileWhitelistChangeRecord;
  // receiver groups user chose when logged in
  alarmStaffGroups?: IAlarmStaffGroupInfo[];
  // start and end dates for camera groups scheduled pause creation
  start?: string;
  end?: string;
}

export interface IValueChangePair<T> {
  // New value of the modified field
  newValue: T;
  // Old value of the modified field
  oldValue: T;
}

// Not in use
export interface IValueChangeSet<T> {
  added: T[];
  removed: T[];
}

export interface IUserAccountChangeRecord {
  roleLevel?: IValueChangePair<number>;
  lockRights?: IValueChangePair<number[]>;
  firstName?: IValueChangePair<string>;
  familyName?: IValueChangePair<string>;
  username?: IValueChangePair<string>;
  gsmNumber?: IValueChangePair<string>;
  email?: IValueChangePair<string>;
  locationId?: IValueChangePair<string>;
  language?: IValueChangePair<string>;
  identityUser?: IValueChangePair<string>;
  accountLocked?: boolean;
  accountUnlocked?: boolean;
  passwordChanged?: boolean;
  addedLocationRights?: string[];
  removedLocationRights?: string[];
  addedReceiverTeams?: string[];
  removedReceiverTeams?: string[];
  addedAlarmRoutes?: string[];
  removedAlarmRoutes?: string[];
}

export interface ServiceTime {
  day: string;
  startTime: string;
  endTime: string;
}

export interface Recipient {
  name: string;
  id: string;
}

export interface Device {
  name: string;
  id: string;
  alertTypes?: AlertType[] | null;
}

export interface AlertType {
  devType: string;
  group: number;
  node: number;
}

export interface RemovedActivationRoute {
  routeId: string;
  routeName: string;
  alertTypes?: AlertType[];
}

export interface IAlarmRouteChangeRecord {
  name?: IValueChangePair<string>;
  locationName?: IValueChangePair<string>;
  deliveryType?: IValueChangePair<string>;
  priority?: IValueChangePair<number>;
  callLoopFailType?: IValueChangePair<string>;
  callCenterName?: IValueChangePair<string>;
  callCenterFirst?: IValueChangePair<boolean>;
  keepAlarmOpenAfterCallCenter?: IValueChangePair<boolean>;
  emaBeforeCallCenter?: IValueChangePair<boolean>;
  callCenterDeliveryType?: boolean;
  fallbackType?: IValueChangePair<string>;
  fallbackCallCenter?: IValueChangePair<string>;
  skipInactiveTeamsForAssistance?: boolean;
  alarmIndicationGroupEnabled?: boolean;
  waitForSmsAcknowledgement?: boolean;
  addedDevices?: any[];
  removedDevices?: any[];
  addedRecipients?: Recipient[];
  removedRecipients?: Recipient[];
  addedServiceTimes?: ServiceTime[];
  removedServiceTimes?: ServiceTime[];
  addedFallBackRecipients?: Recipient[];
  removedFallBackRecipients?: Recipient[];
  isNewCallCenter?: boolean;
}

export interface IAlarmUserGroupChangeRecord {
  groupName?: IValueChangePair<string>;
  locationId?: IValueChangePair<string>;
  locked?: IValueChangePair<boolean | null>;
  addedStaffs?: any[];
  removedStaffs?: any[];
}

export interface ILocationChangeRecord {
  name?: IValueChangePair<string>;
  qualifier?: IValueChangePair<string>;
  address?: IValueChangePair<string>;
  postCode?: IValueChangePair<string>;
  postOffice?: IValueChangePair<string>;
  mainUserGsmNumber?: IValueChangePair<string>;
  contactPhone?: IValueChangePair<string>;
  emaTagOnly?: IValueChangePair<boolean>;
  networkFailureState?: IValueChangePair<boolean>;
}

export interface IEmaSettingsChangeRecord {
  alertTimers?: IValueChangePair<any>;
  alertPriorities?: IValueChangePair<any>;
  alertNotes?: IValueChangePair<any>;
  alarmOptions?: IValueChangePair<any>;
  loginKeys?: IValueChangePair<any>;
  alertUi?: IValueChangePair<any>;
  oldSettings?: any;
  newSettings?: any;
  hasChanges?: boolean;
}

export interface IPersonnelChangeRecord {
  addedPersonnels?: string[];
  removedPersonnels?: string[];
}

export interface ICameraChangeInfo {
  // Camera identifier
  id: string;
  // Name of the camera
  name: string;
  // Motion detection enabled or disabled status of the camera
  motionDetection?: boolean;
  // Anonymized visit enabled or disabled status of the camera
  anonymizedVisit?: boolean;
  // View clear image enabled or disabled status of the camera
  clearImage?: boolean;
  // Visit with alert enabled or disabled status of the camera
  visitWithAlert?: boolean;
  // Snapshot with alert enabled or disabled status of the camera
  snapshotWithAlert?: boolean;
  // Video clip with alert enabled or disabled status of the camera
  videoclipWithAlert?: boolean;
}

export interface IRemovedScheduledVisit {
  startTime: Date;
  durationMinutes: number;
  extendedVisitTimeMinutes: number;
}

export interface IEventTriggerInfo {
  devicePrefixClass: string;
  alarmGroup: string;
  alarmNode: string;
}

export interface ICameraGroupChangeRecord {
  // Previous or new value of the modified camera group name
  groupName?: IValueChangePair<string>;
  // Previous or new value of the modified camera group address
  address?: IValueChangePair<string>;
  // Previous or new value of the modified camera group postal code
  postalCode?: IValueChangePair<string>;
  // Previous or new value of the modified camera group city
  city?: IValueChangePair<string>;
  // Previous or new value of the modified camera group location
  location?: IValueChangePair<string>;
  // Previous or new value of the modified camera group access mode as translation code
  accessModeTranslationCode?: IValueChangePair<string>;
  // Information about the cameras that have been added to the camera group
  addedCameras?: ICameraChangeInfo[];
  // Information about the cameras that have been modified in the camera group
  modifiedCameras?: ICameraChangeInfo[];
  // List of camera that have been removed from the camera group
  removedCameras?: string[];
  // Alert trigger devices that have been added to the camera group
  addedAlertDevices?: string[];
  // Removed alert trigger devices from the camera group
  removedAlertDevices?: string[];
  // Added event triggers to the camera group
  addedEventTriggers?: IEventTriggerInfo[];
  // Removed event triggers from the camera group
  removedEventTriggers?: IEventTriggerInfo[];
  // Added receiver groups to the camera group
  addedReceiverGroups?: string[];
  // Removed receiver groups from the camera group
  removedReceiverGroups?: string[];
  // List of scheduled visits that have been removed along with the camera group
  removedVisits?: IRemovedScheduledVisit[];
  // Previous or new value of the modified camera group visit time in minutes
  visitTimeMinutes?: IValueChangePair<number | null>;
  // Previous or new value of the modified camera group visit grace time in minutes
  graceTimeMinutes?: IValueChangePair<number | null>;
  // Whether visit-by-alarm-type behavior is disabled
  visitByAlarmTypeDisabled?: boolean | null;
  // Previous or new value of the modified camera group origon call
  origon?: IValueChangePair<string>
}

export interface IScheduledVisitEditChangeRecord {
  // Day of the week (1 = Monday, 2 = Tuesday, ..., 7 = Sunday)
  day?: number;
  // New and old start time of the scheduled visit
  startTime?: IValueChangePair<string>;
  // New and old end time of the scheduled visit
  endTime?: IValueChangePair<string>;
  // New and old extended visit time in minutes
  extendedVisitTimeMinutes?: IValueChangePair<number>;
}

export interface IScheduledVisitChangeRecord {
  // List of weekdays as translation codes
  weekdayTranslationCodes: string[];
  // Start time of the scheduled visit
  startTime: string;
  // End time of the scheduled visit
  endTime: string;
  // Extended visit time in minutes
  extendedVisitTimeMinutes: number;
}

export interface ICameraGroupPauseChangeRecord {
  // Whether the camera group is currently paused
  isPaused?: IValueChangePair<boolean | null>;
  // The start timestamp of the scheduled pause creation
  pauseStart?: IValueChangePair<string | null>;
  // The end timestamp of the scheduled pause creation
  pauseEnd?: IValueChangePair<string | null>;
}

export interface IAlarmStaffGroupInfo {
  GroupId: string;
  Active: boolean;
}

export type AlarmRouteChangeRecordField = keyof IAlarmRouteChangeRecord;
export type UserAccountChangeRecordField = keyof IUserAccountChangeRecord;
export type CameraGroupChangeRecordField = keyof ICameraGroupChangeRecord;
export type ScheduledVisitChangeRecordField = keyof IScheduledVisitChangeRecord;
export type AlarmUserGroupChangeRecordField = keyof IAlarmUserGroupChangeRecord;
export type LocationChangeRecordField = keyof ILocationChangeRecord;

/** Before and after of a role change, a role assignment or individual permissions (values are access levels) */
export interface IAccessRoleChangeRecord {
  oldName?: string;
  newName?: string;
  oldGrants?: Record<string, string>;
  newGrants?: Record<string, string>;
}

/**
 * EMA phone whitelist change (request, approve, deny, block or modify). The phone as it is after the change
 * (as it was before a deny or block); old and new values are only present for the fields that changed.
 */
export interface IMobileWhitelistChangeRecord {
  mobileId?: string;
  name?: string;
  gsmNumber?: string;
  oldName?: string;
  newName?: string;
  oldGsmNumber?: string;
  newGsmNumber?: string;
}
